'use server'

import { apiRequest, ApiResponse } from '../request'

export interface DashboardOverview {
    beds: {
        total: number
        occupied: number
        available: number
        cleaning: number
        occupancyRate: number
    }
    admissions: {
        active: number
        today: number
    }
    discharges: {
        today: number
        pending: number
    }
}

export interface UnitOccupancy {
    unitId: number
    unitName: string
    unitType: string
    totalBeds: number
    occupiedBeds: number
    availableBeds: number
    occupancyRate: number
}

export interface AdmissionToday {
    id: number
    admissionNumber: string
    admissionDate: string
    client: {
        id: number
        name: string
    }
    unit: {
        id: number
        name: string
    }
    bed?: {
        id: number
        number: string
    }
    attendingPhysician?: {
        id: number
        name: string
    }
}

export interface DischargeToday {
    id: number
    completedAt: string
    client: {
        id: number
        name: string
    }
    admission: {
        id: number
        unit: {
            id: number
            name: string
        }
    }
}

export interface PendingCheck {
    id: number
    scheduledTime: string
    item: {
        medicationName: string
        dose: string
        route: string
    }
    prescription: {
        client: {
            id: number
            name: string
        }
        admission: {
            bed?: {
                id: number
                number: string
            }
            unit?: {
                id: number
                name: string
            }
        }
    }
}

export interface PendingEvolution {
    id: number
    admissionNumber: string
    client: {
        id: number
        name: string
    }
    unit: {
        id: number
        name: string
    }
    bed?: {
        id: number
        number: string
    }
}

export interface CriticalAlert {
    id: number
    parameter: string
    value: number
    severity: string
    message: string
    createdAt: string
    admission?: {
        client: {
            id: number
            name: string
        }
    }
}

export async function getDashboardOverview(
    companyId: number,
    hospitalId?: number,
): Promise<ApiResponse<DashboardOverview>> {
    return apiRequest<DashboardOverview>(
        `hospital/dashboard/overview/${companyId}`,
        { params: { hospitalId } }
    )
}

export async function getOccupancyByUnit(
    companyId: number,
    hospitalId?: number,
): Promise<ApiResponse<UnitOccupancy[]>> {
    return apiRequest<UnitOccupancy[]>(
        `hospital/dashboard/occupancy/${companyId}`,
        { params: { hospitalId } }
    )
}

export async function getAdmissionsToday(
    companyId: number,
    hospitalId?: number,
): Promise<ApiResponse<AdmissionToday[]>> {
    return apiRequest<AdmissionToday[]>(
        `hospital/dashboard/admissions-today/${companyId}`,
        { params: { hospitalId } }
    )
}

export async function getDischargesToday(
    companyId: number,
    hospitalId?: number,
): Promise<ApiResponse<DischargeToday[]>> {
    return apiRequest<DischargeToday[]>(
        `hospital/dashboard/discharges-today/${companyId}`,
        { params: { hospitalId } }
    )
}

export async function getPendingChecks(
    companyId: number,
    hospitalId?: number,
): Promise<ApiResponse<PendingCheck[]>> {
    return apiRequest<PendingCheck[]>(
        `hospital/dashboard/pending-checks/${companyId}`,
        { params: { hospitalId } }
    )
}

export async function getPendingEvolutions(
    companyId: number,
    hospitalId?: number,
): Promise<ApiResponse<PendingEvolution[]>> {
    return apiRequest<PendingEvolution[]>(
        `hospital/dashboard/pending-evolutions/${companyId}`,
        { params: { hospitalId } }
    )
}

export async function getCriticalAlerts(
    companyId: number,
    hospitalId?: number,
): Promise<ApiResponse<CriticalAlert[]>> {
    return apiRequest<CriticalAlert[]>(
        `hospital/dashboard/critical-alerts/${companyId}`,
        { params: { hospitalId } }
    )
}

export async function getDashboardVitalSignsAlerts(
    companyId: number,
    hospitalId?: number,
): Promise<ApiResponse<CriticalAlert[]>> {
    return apiRequest<CriticalAlert[]>(
        `hospital/dashboard/vital-signs-alerts/${companyId}`,
        { params: { hospitalId } }
    )
}
